import { useContext, useEffect } from "react";
import { useRouter } from "next/router";
import { useMutation, useQuery } from "react-query";
import cartService from "services/cart";
import { useAppDispatch, useAppSelector } from "hooks/useRedux";
import { useAuth } from "contexts/auth/auth.context";
import {
  selectCurrency,
  setCurrency,
  setDefaultCurrency,
} from "redux/slices/currency";
import currencyService from "services/currency";
import { Currency, Langauge } from "interfaces";
import languageService from "services/language";
import dynamic from "next/dynamic";
import informationService from "services/information";
import { useSettings } from "contexts/settings/settings.context";
import ErrorBoundary from "containers/errorBoundary/errorBoundary";
import { ThemeContext } from "contexts/theme/theme.context";
import Footer from "./footer/footer";
import translationService from "services/translations";
import useLocale from "hooks/useLocale";
import createSettings from "utils/createSettings";
import dayjs from "dayjs";
import "dayjs/locale/nl";
import "dayjs/locale/pl";
import {
  updateCart,
  updateList,
  setShopId,
  clearFullCart,
} from "redux/slices/carts";
import { batch } from "react-redux";
import useClearCart from "hooks/useClearCart";
import getCartListFromServerCart from "utils/getCartListFromServerCart";
import likeService from "services/like";
import { setLikedList, updateLikedList } from "redux/slices/liked";
import { useTranslation } from "react-i18next";
import { setCookie } from "utils/session";
import cls from "./layout.module.scss";

const PushNotification = dynamic(
  () => import("containers/pushNotification/pushNotification"),
);
const ProfileHeader = dynamic(() => import("./profileHeader/profileHeader"));
const MobileHeader = dynamic(() => import("./mobileHeader/mobileHeader"));
const Header = dynamic(() => import("./header/header"));

type LayoutProps = {
  children: any;
  locale: string;
};

const profileRoutes = [
  "checkout",
  "profile",
  "settings",
  "help",
  "orders/",
  "be-seller",
];

export default function Layout({ children, locale }: LayoutProps) {
  const { i18n } = useTranslation();
  const { pathname } = useRouter();
  const { addResourceBundle, changeLanguage } = useLocale();
  const isProfileRoute = profileRoutes.find((item) => pathname.includes(item));
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAuth();
  const { list, shopId } = useAppSelector((state) => state.carts);
  const { productList, shopList } = useAppSelector((state) => state.liked);
  const currency = useAppSelector(selectCurrency);
  const { updateSettings } = useSettings();
  const { isDarkMode, setDirection } = useContext(ThemeContext);
  const router = useRouter();
  const isShopDetailPage = router.pathname.startsWith("/shop/");

  useQuery(
    ["translation", locale],
    () => translationService.getAll({ lang: locale }),
    {
      enabled: !!locale,
      onSuccess: (data) => {
        addResourceBundle(locale, "translation", data.data);
        changeLanguage(locale);
      },
    },
  );

  useQuery("currencies", () => currencyService.getAll(), {
    onSuccess: (data) => {
      const activeCurrency = data.data.find((item: Currency) => item.default);
      const savedCurrency = data.data.find(
        (item: Currency) => item.id === currency?.id,
      );
      dispatch(setDefaultCurrency(activeCurrency));
      if (savedCurrency) {
        dispatch(setCurrency(savedCurrency));
        setCookie("currency_id", savedCurrency?.id);
      } else {
        dispatch(setCurrency(activeCurrency));
        setCookie("currency_id", activeCurrency?.id);
      }
    },
  });

  useQuery("languages", () => languageService.getAllActive(), {
    onSuccess: (data) => {
      const isRTL = !!data?.data.find((item: Langauge) => item.locale == locale)
        ?.backward;
      setDirection(isRTL ? "rtl" : "ltr");
    },
  });

  useQuery({
    queryKey: ["likedShopsLayout", i18n.language],
    queryFn: () => likeService.getAll({ type: "shop", perPage: 100 }),
    onSuccess: (res) => {
      const unSentShopList = shopList
        .filter((item) => !item.sent)
        .map((item) => ({
          type: "shop",
          type_id: item.shopId,
        }));
      if (unSentShopList.length) {
        likeMany({ types: unSentShopList });
      }
      const likedShops =
        res?.data?.map((item: any) => ({
          shopId: item?.id,
          sent: true,
        })) || [];
      dispatch(
        setLikedList({
          type: "shop",
          list: [
            ...likedShops,
            ...unSentShopList.map((item) => ({
              shopId: item.type_id,
              sent: true,
            })),
          ],
        }),
      );
    },
    enabled: isAuthenticated,
  });

  useQuery({
    queryKey: ["likedProductsLayout", i18n.language, currency?.id],
    queryFn: () =>
      likeService.getAll({
        type: "product",
        currency_id: currency?.id,
        perPage: 100,
      }),
    onSuccess: (res) => {
      const unSentProductsList = productList
        .filter((item) => !item.sent)
        .map((item) => ({
          type: "product",
          type_id: item.productId,
        }));
      if (unSentProductsList.length) {
        likeMany({ types: unSentProductsList });
      }
      const likedProducts =
        res?.data?.map((item: any) => ({
          productId: item?.id,
          sent: true,
        })) || [];
      dispatch(
        setLikedList({
          type: "product",
          list: [
            ...likedProducts,
            ...unSentProductsList.map((item) => ({
              productId: item.type_id,
              sent: true,
            })),
          ],
        }),
      );
    },
    enabled: isAuthenticated,
  });

  useQuery(
    ["cartLayout", currency?.id],
    () => cartService.get({ currency_id: currency?.id }),
    {
      onSuccess: (res) => {
        const serverShopId = res?.data?.shop?.id;
        const userCart = res?.data?.user_carts?.find(
          (userCart) => userCart?.user_id === user?.id,
        );
        if (list.length) {
          if (shopId === serverShopId) {
            handleInsertProducts();
          } else if (userCart?.cart_id) {
            deleteServerCart([userCart.cart_id]);
          }
        } else {
          batch(() => {
            dispatch(updateCart(res?.data));
            dispatch(updateList(getCartListFromServerCart(userCart)));
            dispatch(setShopId(serverShopId));
          });
        }
      },
      onError: () => {
        if (list.length) {
          handleInsertProducts();
        } else {
          dispatch(clearFullCart());
        }
      },
      retry: false,
      enabled: isAuthenticated,
    },
  );

  useQuery("settings", () => informationService.getSettings(), {
    onSuccess: (data) => {
      const obj = createSettings(data.data);
      updateSettings({
        payment_type: obj.payment_type,
        instagram_url: obj.instagram,
        facebook_url: obj.facebook,
        twitter_url: obj.twitter,
        referral_active: obj.referral_active,
        otp_expire_time: obj.otp_expire_time,
        customer_app_android: obj.customer_app_android,
        customer_app_ios: obj.customer_app_ios,
        delivery_app_android: obj.delivery_app_android,
        delivery_app_ios: obj.delivery_app_ios,
        vendor_app_android: obj.vendor_app_android,
        vendor_app_ios: obj.vendor_app_ios,
        group_order: obj.group_order,
        footer_text: obj.footer_text,
        ui_type: obj.ui_type,
        address_text: obj.address,
        phone: obj.phone,
        email: obj.email,
        reservation_time_durations: obj.reservation_time_durations,
        reservation_before_time: obj.reservation_before_time,
        min_reservation_time: obj.min_reservation_time,
        active_parcel: obj.active_parcel,
        before_order_phone_required: obj.before_order_phone_required,
        reservation_enable_for_user: obj.reservation_enable_for_user,
        google_map_key: obj.google_map_key,
        favicon: obj.favicon,
        title: obj.title,
        generalSettingsLocation: obj.location,
        logo: obj.logo,
      });
    },
  });

  const { mutate: insertProducts } = useMutation({
    mutationFn: (data: any) => cartService.insert(data),
    onSuccess: (res) => {
      const serverShopId = res?.data?.shop?.id;
      const userCart = res?.data?.user_carts?.find(
        (userCart) => userCart?.user_id === user?.id,
      );

      batch(() => {
        dispatch(updateCart(res?.data));
        dispatch(updateList(getCartListFromServerCart(userCart)));
        dispatch(setShopId(serverShopId));
      });
    },
  });

  const { mutate: deleteServerCart } = useClearCart({
    clearLocalCart: false,
    onSuccess: () => {
      handleInsertProducts();
    },
  });

  const { mutate: likeMany } = useMutation({
    mutationFn: (body: any) => likeService.likeMany(body),
    onSuccess: (_, body) => {
      const newShopList: any[] = [];
      const newProductList: any[] = [];
      body.types.forEach((item: any) => {
        if (item.type === "shop") {
          newShopList.push({ shopId: item.type_id, sent: true });
        } else if (item.type === "product") {
          newProductList.push({ productId: item.type_id, sent: true });
        }
      });
      batch(() => {
        dispatch(updateLikedList({ type: "shop", list: newShopList }));
        dispatch(updateLikedList({ type: "product", list: newProductList }));
      });
    },
  });

  const handleInsertProducts = () => {
    const body = {
      shop_id: shopId,
      currency_id: currency?.id,
      products: list.map((item) => ({
        stock_id: item.stockId,
        quantity: item.quantity,
      })),
    };
    insertProducts(body);
  };

  useEffect(() => {
    dayjs.locale(locale);
  }, [locale]);

  return (
    <ErrorBoundary isDarkMode={isDarkMode}>
      <div className="layout-container">
        {isProfileRoute ? (
          <ProfileHeader />
        ) : (
          <>
            <div className={cls.header}>
              <Header />
            </div>
            <div className={cls.mobileHeader}>
              <MobileHeader isShopDetailPage={isShopDetailPage} />
            </div>
          </>
        )}
        {children}
        {isAuthenticated && <PushNotification />}
        <Footer />
      </div>
    </ErrorBoundary>
  );
}
